import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { hasPermission } from '@/types';
import { NavLink, useParams } from 'react-router-dom';
import { studies, workflow, users as usersApi, pdf as pdfApi,
         studyRejections, studyReview,
         type AssignableUser, type BundleManifest, type BundleManifestEntry,
         type AiVerificationReport, type StudyRejectionDto,
         type StudyVersionDto, type SectionDiff,
         type CompletenessReport, type CompletenessIssue } from '@/api/endpoints';
import type { StudyStep, StudyDetail as OprDetail } from '@/types';
import { Icon } from '@/components/Icon';
import { TeamMembers } from '@/components/TeamMembers';
import { StudyRequiredDocsCard } from '@/components/StudyRequiredDocsCard';
import { StudyRequiredLayersCard } from '@/components/StudyRequiredLayersCard';
import { StudyAttachmentsCard } from '@/components/StudyAttachmentsCard';
import { AnalizaRyzykaCard } from '@/components/analiza/AnalizaRyzykaCard';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { formatMoney } from '@/lib/money';

function krokStepClass(status: string): string {
  if (status === 'COMPLETED') return 'wf-step wf-step-done';
  if (status === 'IN_PROGRESS') return 'wf-step wf-step-current';
  if (status === 'SKIPPED') return 'wf-step wf-step-skipped';
  return 'wf-step';
}

function stepStatusLabel(status: string): string {
  switch (status) {
    case 'TODO': return 'do zrobienia';
    case 'IN_PROGRESS': return 'w toku';
    case 'COMPLETED': return 'zakończone';
    case 'SKIPPED': return 'pominięte';
    default: return status.toLowerCase();
  }
}

function plPlural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return many;
  if (last >= 2 && last <= 4) return few;
  return many;
}

function formatTimeAgo(iso?: string): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  if (ms < 60_000) return 'przed chwilą';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} ${plPlural(minutes, 'minutę', 'minuty', 'minut')} temu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${plPlural(hours, 'godzinę', 'godziny', 'godzin')} temu`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${plPlural(days, 'dzień', 'dni', 'dni')} temu`;
  const date = new Date(iso);
  return `od ${date.toLocaleDateString('pl-PL')}`;
}

/** Pełny timestamp w formacie pl-PL: "07.05.2026, 11:23". Null gdy brak/niepoprawne ISO. */
function formatDateTime(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

export function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<OprDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [dispatchDateInput, setDispatchDateInput] = useState<string>('');
  const [savingDispatch, setSavingDispatch] = useState(false);
  // Pętla rewizji urzędu — modal poprawek + busy flag dla obu akcji review.
  const [reviewBusy, setReviewBusy] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const revisionGuard = useUnsavedChanges({
    value: revisionReason, open: revisionDialogOpen, busy: reviewBusy,
    onClose: () => { setRevisionDialogOpen(false); setRevisionReason(''); },
  });

  const load = () => {
    if (!id) return;
    setErr(null);
    studies.get(Number(id)).then(setData).catch(e => setErr(String(e)));
  };

  useEffect(() => {
    setData(null);
    load();
    usersApi.assignable().then(setAssignableUsers).catch(() => { /* nonfatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setDispatchDateInput(data?.summary.dispatchDate ?? '');
  }, [data?.summary.dispatchDate]);

  const saveDispatchDate = async (value: string) => {
    if (!data) return;
    setSavingDispatch(true);
    try {
      await studies.setDispatchDate(data.summary.id, value || null);
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setSavingDispatch(false);
    }
  };

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!data) return <div className="text-ink-400">Ładowanie…</div>;

  const { summary, intake, steps, tasks, drafts, contract, contractHighlights, percent } = data;
  // AWAITING_REVIEW (oczekuje na decyzję urzędu) też blokuje edycję — operator musi
  // najpierw zarejestrować decyzję urzędu (zaakceptowane lub poprawki). REVISION_NEEDED
  // jest edytowalne — to przewidziany scenariusz pracy nad poprawkami.
  const isReadOnly = summary.status === 'COMPLETED'
    || summary.status === 'CANCELLED'
    || summary.status === 'AWAITING_REVIEW';

  const onRozpocznij = (k: StudyStep) => {
    setBusy(k.id);
    workflow.rozpocznij(k.id).then(load).catch(e => setErr(String(e))).finally(() => setBusy(null));
  };
  const onZakoncz = (k: StudyStep) => {
    setBusy(k.id);
    workflow.zakoncz(k.id).then(load).catch(e => setErr(String(e))).finally(() => setBusy(null));
  };
  const onAssign = (k: StudyStep, userId: number | null) => {
    setBusy(k.id);
    workflow.assign(k.id, userId)
      .then(load)
      .catch(e => setErr(String(e)))
      .finally(() => { setBusy(null); setEditingStepId(null); });
  };

  const onApproveByAuthority = () => {
    if (!confirm('Potwierdzić, że urząd zaakceptował opracowanie? '
                + 'Po akceptacji opracowanie zostanie zamknięte (status COMPLETED).')) return;
    setReviewBusy(true);
    studyReview.approve(Number(id))
      .then(load)
      .catch(e => setErr(String(e)))
      .finally(() => setReviewBusy(false));
  };

  const onRequestRevision = () => {
    const reason = revisionReason.trim();
    if (!reason) { setErr('Opis uwag urzędu jest wymagany'); return; }
    setReviewBusy(true);
    studyReview.requestChanges(Number(id), reason)
      .then(() => {
        setRevisionDialogOpen(false);
        setRevisionReason('');
        return load();
      })
      .catch(e => setErr(String(e)))
      .finally(() => setReviewBusy(false));
  };


  return (
    <div className="max-w-[1400px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[12px] text-ink-500 shrink-0">{summary.number}</span>
            <h1 className="m-0 truncate min-w-0 flex-1" title={summary.title}>{summary.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className="pill pill-earth">{summary.typeAbbreviation}-{summary.typeCode}</span>
            <span className={'pill ' + (summary.authority === 'REGIONAL' ? 'pill-marsz' : 'pill-star')}>
              <span className="pill-dot"/>{summary.authorityName}
            </span>
            {intake && (
              <NavLink to={`/intakes/${intake.id}`} className="pill pill-water no-underline">
                <Icon name="droplet" className="w-3 h-3 mr-1"/>{intake.name}
              </NavLink>
            )}
            {summary.deadline && <span className="text-ink-500">Deadline: <span className="font-mono">{summary.deadline}</span></span>}
            <span className={'pill pill-' + (summary.statusColor || 'water') + ' ml-auto'}>{summary.statusName}</span>
          </div>
        </div>
        <NavLink to="/studies" className="btn btn-secondary btn-sm shrink-0">← Wszystkie</NavLink>
      </header>

      {summary.status === 'AWAITING_REVIEW' && (
        <div className="mb-4 px-4 py-3 rounded-md border-l-[4px] border-amber-500 bg-amber-50 flex items-start gap-3">
          <Icon name="bell" className="w-4 h-4 text-amber-700 mt-1"/>
          <div className="flex-1 text-[13px] text-amber-900">
            <div className="font-semibold">Opracowanie wysłane — czeka na decyzję urzędu</div>
            <div className="text-[12px] text-amber-700 mt-0.5">
              Edycja draftów jest zablokowana do czasu zarejestrowania decyzji. Wybierz akcję poniżej
              gdy otrzymasz odpowiedź z urzędu.
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setRevisionDialogOpen(true)} disabled={reviewBusy}
                    className="btn btn-secondary btn-sm border-red-300 text-red-700">
              <Icon name="x" className="w-3.5 h-3.5"/> Wymaga poprawek
            </button>
            <button onClick={onApproveByAuthority} disabled={reviewBusy}
                    className="btn btn-primary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5"/> Urząd zaakceptował
            </button>
          </div>
        </div>
      )}

      {summary.status === 'REVISION_NEEDED' && (
        <RevisionFeedbackBanner studyId={summary.id}/>
      )}

      {(summary.status === 'COMPLETED' || summary.status === 'CANCELLED') && (
        <div className="mb-4 px-4 py-3 rounded-md border-l-[4px] border-amber-500 bg-amber-50 flex items-center gap-3">
          <Icon name="check" className="w-4 h-4 text-amber-700"/>
          <div className="flex-1 text-[13px] text-amber-900">
            <div className="font-semibold">
              {summary.status === 'COMPLETED'
                ? 'Opracowanie zakończone — tryb tylko do odczytu'
                : 'Opracowanie anulowane — tryb tylko do odczytu'}
            </div>
            <div className="text-[12px] text-amber-700">
              Wszystkie pola i akcje są zablokowane. Pobieranie operatu, DOCX-a, wniosku i kompletnej paczki ZIP
              pozostaje dostępne.
            </div>
          </div>
        </div>
      )}

      {revisionDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
             onClick={revisionGuard.requestClose} {...revisionGuard.dialogProps}>
          {revisionGuard.confirmation}
          <div className="bg-white rounded-lg shadow-2xl max-w-[700px] w-full mt-12" onClick={e => e.stopPropagation()}>
            <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
              <Icon name="x" className="w-4 h-4 text-red-600"/>
              <h2 className="text-[16px] font-semibold m-0">Urząd wymaga poprawek</h2>
              <button onClick={revisionGuard.requestClose} disabled={reviewBusy}
                      className="ml-auto text-ink-400 hover:text-ink-700">
                <Icon name="x"/>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[12px] text-ink-600">
                Wpisz uwagi urzędu (zostaną zapisane jako odrzucenie i widoczne w historii decyzji).
                Po zarejestrowaniu — kroki K11 (Podpis) i K12 (Wysyłka) zostaną zresetowane,
                opracowanie wróci do trybu edycji draftów.
              </div>
              <textarea
                value={revisionReason}
                onChange={e => setRevisionReason(e.target.value)}
                rows={8}
                disabled={reviewBusy}
                placeholder="np. „Sekcja Warunki hydrogeologiczne — uzupełnić dane jakości wód surowych z 2024 r. Sekcja Strefy ochronne — błędna interpretacja art. 133 PrW…"
                className="textarea w-full text-[13px]"
                autoFocus
              />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={revisionGuard.requestClose} disabled={reviewBusy}
                        className="btn btn-secondary btn-sm">Anuluj</button>
                <button onClick={onRequestRevision} disabled={reviewBusy || !revisionReason.trim()}
                        className="btn btn-primary btn-sm">
                  <Icon name="check" className="w-3.5 h-3.5"/>
                  {reviewBusy ? 'Zapisuję…' : 'Zarejestruj uwagi i odblokuj edycję'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const activeStep = steps.find(s => s.status === 'IN_PROGRESS');
        const activeAgo = activeStep ? formatTimeAgo(activeStep.startedAt) : null;
        return (
          <div className="card mb-4">
            <div className="card-head">
              <div className="card-title">Workflow 12 kroków</div>
              {activeStep && (
                <span className="ml-3 inline-flex items-center gap-1.5 pill pill-water" title={activeStep.codeString + ' · ' + activeStep.name}>
                  <Icon name="users" className="w-3 h-3"/>
                  <span className="font-mono text-[11px]">{activeStep.codeString}</span>
                  <span className="text-[11px] font-semibold">{activeStep.name}</span>
                  <span className="text-[11px] opacity-80">·</span>
                  <span className="text-[11px]">
                    {activeStep.assigneeFirstName ? activeStep.assigneeFirstName : 'nieprzypisane'}
                  </span>
                  {activeAgo && <span className="text-[10px] opacity-80">· {activeAgo}</span>}
                </span>
              )}
              <span className="ml-auto text-[12px] text-ink-500 font-mono">{percent}%</span>
            </div>
            <div className="card-body">
              <div className="flex gap-0.5 mb-4">
                {steps.map(k => (
                  <div key={k.id} className={krokStepClass(k.status)} title={k.codeString + ' · ' + k.name}/>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                {steps.map(k => {
                  const ago = k.status === 'IN_PROGRESS' ? formatTimeAgo(k.startedAt)
                            : k.status === 'COMPLETED' ? formatTimeAgo(k.completedAt)
                            : null;
                  const agoLabel = k.status === 'IN_PROGRESS' ? 'rozpoczęte ' : k.status === 'COMPLETED' ? 'ukończone ' : '';
                  return (
                    <div key={k.id} className="border-b border-ink-100 last:border-b-0">
                    <div className="flex items-center gap-3 py-2">
                      <span className="font-mono text-[11px] text-ink-500 w-10">{k.codeString}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-ink-800 truncate">{k.name}</div>
                        {ago && (
                          <div className="text-[10px] text-ink-400 mt-0.5">{agoLabel}{ago}</div>
                        )}
                      </div>
                      {editingStepId === k.id ? (
                        <span className="inline-flex items-center gap-1">
                          <SearchableSelect
                            value={k.assigneeId != null ? String(k.assigneeId) : ''}
                            onChange={v => onAssign(k, v ? Number(v) : null)}
                            disabled={busy === k.id}
                            className="select-sm text-[11px] min-w-[180px]"
                          >
                            <option value="">— nieprzypisane —</option>
                            {assignableUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.fullName}</option>
                            ))}
                          </SearchableSelect>
                          <button onClick={() => setEditingStepId(null)} className="text-ink-400 hover:text-ink-700 p-0.5"
                                  title="Anuluj">
                            <Icon name="x" className="w-3 h-3"/>
                          </button>
                        </span>
                      ) : (
                        <span className={'inline-flex items-center gap-1.5 text-[11px] ' + (k.assigneeFirstName ? 'text-ink-700' : 'text-ink-400 italic')}>
                          {k.assigneeFirstName ? (
                            <>
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink-100 text-[9px] font-bold text-ink-700"
                                    title={k.assigneeFirstName}>
                                {k.assigneeInitials ?? '??'}
                              </span>
                              <span>{k.assigneeFirstName}</span>
                            </>
                          ) : (
                            <>
                              <Icon name="users" className="w-3 h-3"/>
                              nieprzypisane
                            </>
                          )}
                          {!isReadOnly && k.status !== 'COMPLETED' && k.status !== 'SKIPPED' && (
                            <button
                              onClick={() => setEditingStepId(k.id)}
                              disabled={busy === k.id}
                              className="text-ink-400 hover:text-water-600 p-0.5 ml-0.5"
                              title={k.assigneeFirstName ? 'Zmień osobę' : 'Przypisz osobę'}
                            >
                              <Icon name="edit" className="w-3 h-3"/>
                            </button>
                          )}
                        </span>
                      )}
                      <span className={'pill ' + (k.status === 'COMPLETED' ? 'pill-green' : k.status === 'IN_PROGRESS' ? 'pill-water' : k.status === 'SKIPPED' ? 'pill-amber' : '')}>
                        {stepStatusLabel(k.status)}
                      </span>
                      {!isReadOnly && k.status === 'TODO' && (
                        <button disabled={busy === k.id} onClick={() => onRozpocznij(k)} className="btn btn-secondary btn-sm">Rozpocznij</button>
                      )}
                      {!isReadOnly && k.status === 'IN_PROGRESS' && (
                        <button disabled={busy === k.id} onClick={() => onZakoncz(k)} className="btn btn-primary btn-sm">Zakończ</button>
                      )}
                    </div>
                    {k.codeString === 'K12' && (
                      <div className="flex items-center gap-2 pl-[52px] pb-2 text-[12px]">
                        <span className="text-ink-500">Data wysyłki:</span>
                        <input
                          type="date"
                          value={dispatchDateInput}
                          disabled={savingDispatch || isReadOnly}
                          onChange={e => setDispatchDateInput(e.target.value)}
                          onBlur={() => {
                            if (isReadOnly) return;
                            const current = data?.summary.dispatchDate ?? '';
                            if (dispatchDateInput !== current) saveDispatchDate(dispatchDateInput);
                          }}
                          className="input input-sm w-44"
                        />
                        {savingDispatch && <span className="text-ink-400">zapisywanie…</span>}
                        {!savingDispatch && !isReadOnly && data?.summary.dispatchDate && (
                          <button
                            type="button"
                            onClick={() => { setDispatchDateInput(''); saveDispatchDate(''); }}
                            className="text-ink-400 hover:text-ink-700 p-0.5"
                            title="Wyczyść datę"
                          >
                            <Icon name="x" className="w-3 h-3"/>
                          </button>
                        )}
                        {!savingDispatch && !data?.summary.dispatchDate && (
                          <span className="text-ink-400 italic">— niewypełnione</span>
                        )}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      <StudyVersionsCard studyId={summary.id}/>
      <RejectionsCard studyId={summary.id} readOnly={isReadOnly}/>

      <StudyRequiredDocsCard studyId={summary.id} onDraftsGenerated={load} readOnly={isReadOnly}/>

      <StudyRequiredLayersCard studyId={summary.id}/>

      <StudyAttachmentsCard studyId={summary.id} readOnly={isReadOnly}/>

      {summary.type === 'ANALIZA_RYZYKA_6' && (
        <AnalizaRyzykaCard studyId={summary.id} readOnly={isReadOnly}/>
      )}

      {steps.some(s => s.codeString === 'K10B') && (
        <AiVerificationCard studyId={summary.id} readOnly={isReadOnly}/>
      )}

      {summary.typeAbbreviation === 'Op-wod' && (
        <CompletenessCard studyId={summary.id} typeCode={summary.typeCode}/>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Zadania</div><span className="ml-auto text-[12px] text-ink-400">{tasks.length}</span></div>
            <div className="card-body !px-[18px] !py-1.5">
              {tasks.length === 0 && <div className="text-ink-400 py-3">Brak zadań.</div>}
              {tasks.map(z => (
                <div key={z.id} className="flex items-start gap-2.5 py-2.5 border-b border-ink-100 last:border-b-0">
                  <span className={'sdot ' + (z.priority === 'CRITICAL' ? 'sdot-red' : z.priority === 'HIGH' ? 'sdot-amber' : 'sdot-blue')}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13px]">{z.title}</div>
                    <div className="text-[11px] text-ink-400">{z.statusName}{z.deadline && ' · deadline: ' + z.deadline}</div>
                  </div>
                  {z.stepCode && <span className="pill pill-earth">{z.stepCode}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="flex items-center gap-2">
                <div className="sdot sdot-ai"/>
                <div className="card-title">Drafty AI</div>
              </div>
              <span className="ml-auto text-[12px] text-ink-400">{drafts.length}</span>
            </div>
            <div className="card-body !px-[18px] !py-1.5">
              {drafts.length === 0 && <div className="text-ink-400 py-3">Brak draftów.</div>}
              {drafts.map(d => {
                const isAccepted = d.status === 'ACCEPTED';
                const onToggleAccept = async () => {
                  try {
                    if (isAccepted) {
                      if (!confirm(`Cofnąć akceptację draftu „${d.section}"? Wróci do edycji.`)) return;
                      const { ai } = await import('@/api/endpoints');
                      await ai.unaccept(d.id);
                    } else {
                      const { ai } = await import('@/api/endpoints');
                      await ai.accept(d.id);
                    }
                    load();
                  } catch (err) {
                    alert(err instanceof Error ? err.message : String(err));
                  }
                };
                // <button> NIE może być wewnątrz <NavLink> (renderowane jako <a>) — HTML5 zabrania
                // zagnieżdżania interaktywnych elementów. Wiersz to flex z dwoma niezależnymi
                // dziećmi: NavLink (klikalny obszar opisu) + osobny <button> (akcja).
                return (
                  <div key={d.id}
                       className="flex items-center gap-2.5 py-2.5 border-b border-ink-100 last:border-b-0 -mx-[18px] px-[18px] hover:bg-ink-50">
                    <NavLink
                      to={`/ai-editor?studyId=${summary.id}&draftId=${d.id}`}
                      className="flex items-center gap-2.5 flex-1 min-w-0 no-underline text-inherit"
                    >
                      <div className={'w-9 h-9 rounded-lg grid place-items-center shrink-0 '
                                    + (isAccepted ? 'bg-green-100 text-green-700' : 'bg-ai-500/10 text-ai-600')}>
                        <Icon name={isAccepted ? 'check' : 'sparkles'}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13px] text-ink-900 flex items-center gap-2">
                          {d.section}
                          {isAccepted && <span className="pill pill-green text-[10px]">🔒 zatwierdzony</span>}
                        </div>
                        <div className="text-[11px] text-ink-400">
                          {d.status} · {d.model}
                          {d.createdAt && (
                            <span className="ml-1.5" title={formatDateTime(d.createdAt) ?? ''}>
                              · wygenerowano {formatTimeAgo(d.createdAt) ?? formatDateTime(d.createdAt)}
                            </span>
                          )}
                        </div>
                        {isAccepted && d.acceptedByName && (
                          <div className="text-[11px] text-green-700 mt-0.5">
                            ✓ Zatwierdził <strong>{d.acceptedByName}</strong>
                            {d.acceptedAt && (
                              <span title={formatDateTime(d.acceptedAt) ?? ''}>
                                {' · '}{formatTimeAgo(d.acceptedAt) ?? formatDateTime(d.acceptedAt)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </NavLink>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={onToggleAccept}
                        className={'btn btn-sm shrink-0 ' + (isAccepted ? 'btn-secondary' : 'btn-primary')}
                        title={isAccepted ? 'Cofnij akceptację — draft wróci do edycji' : 'Zatwierdź draft — zablokuje edycję'}>
                        {isAccepted ? 'Cofnij' : 'Zatwierdź'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {intake?.client && (
            <NavLink to={`/klienci/${intake.client.id}`}
                     className="card no-underline text-inherit hover:border-water-500 hover:shadow-sm transition group">
              <div className="card-head">
                <div className="card-title">Klient</div>
                <Icon name="chevron" className="ml-auto w-3.5 h-3.5 text-ink-300 group-hover:text-water-500"/>
              </div>
              <div className="card-body text-[12px] flex flex-col gap-1.5">
                <div className="font-semibold text-ink-900 text-[14px] flex items-center gap-1.5">
                  <Icon name="users" className="w-4 h-4 text-water-600"/>
                  {intake.client.name}
                </div>
                {intake.client.nip && (
                  <div><span className="text-ink-400">NIP:</span> <span className="font-mono text-ink-700">{intake.client.nip}</span></div>
                )}
                {intake.client.email && (
                  <div className="text-ink-700 truncate" title={intake.client.email}>{intake.client.email}</div>
                )}
                {intake.client.phone && (
                  <div className="font-mono text-ink-600">{intake.client.phone}</div>
                )}
                {(intake.client.addressLine1 || intake.client.city) && (
                  <div className="text-ink-500 text-[11px]">
                    {intake.client.addressLine1}
                    {intake.client.addressLine1 && (intake.client.city || intake.client.postalCode) ? ', ' : ''}
                    {[intake.client.postalCode, intake.client.city].filter(Boolean).join(' ')}
                  </div>
                )}
              </div>
            </NavLink>
          )}
          <div className="card">
            <div className="card-head"><div className="card-title">Zespół</div></div>
            <div className="card-body text-[12px] text-ink-600 flex flex-col gap-1">
              {summary.managerFirstName && <div><span className="text-ink-400">Kierownik:</span> {summary.managerFirstName}</div>}
              {summary.geologistFirstName && <div><span className="text-ink-400">Geolog:</span> {summary.geologistFirstName}</div>}
            </div>
          </div>
          {contract && user?.permissions?.includes('CONTRACT_VIEW') ? (
            <NavLink to={`/umowa/${contract.id}`}
                     className="card no-underline text-inherit hover:border-water-500 hover:shadow-sm transition group">
              <div className="card-head">
                <div className="card-title">Umowa</div>
                <Icon name="chevron" className="ml-auto w-3.5 h-3.5 text-ink-300 group-hover:text-water-500"/>
              </div>
              <div className="card-body">
                <div className="font-mono text-[12px] text-water-600 mb-1">{contract.number}</div>
                <div className="font-semibold mb-1">{contract.variantName}</div>
                {contract.amount != null && user?.permissions?.includes('PRICING_VIEW') && (
                  <div className="font-mono text-[14px] text-ink-800">
                    {formatMoney(contract.amount)} zł
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {contract.signed
                    ? <span className="pill pill-green">Podpisana</span>
                    : contract.draft
                    ? <span className="pill pill-amber">Draft</span>
                    : <span className="pill pill-water">Zaakceptowana</span>}
                  {contract.aiGenerated && <span className="pill pill-ai">AI</span>}
                </div>
              </div>
            </NavLink>
          ) : !contractHighlights && hasPermission(user, 'CONTRACT_CREATE') ? (
            <div className="card">
              <div className="card-head"><div className="card-title">Umowa</div></div>
              <div className="card-body">
                <NavLink to={`/contract-wizard?studyId=${summary.id}`} className="btn btn-secondary btn-sm w-full justify-center">
                  <Icon name="plus" className="w-3.5 h-3.5"/> Kreator umowy
                </NavLink>
              </div>
            </div>
          ) : null}

          {/* Najważniejsze z umowy — bez kwot i treści, dla każdego, kto widzi temat (biuro, asystent, geolog). */}
          {contractHighlights && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Najważniejsze z umowy</div>
              </div>
              <div className="card-body text-[12px]">
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="font-mono text-ink-500">{contractHighlights.number}</span>
                  <span className={'pill ' + (contractHighlights.status === 'Podpisana' ? 'pill-green' : 'pill-amber')}>
                    {contractHighlights.status}
                  </span>
                  {contractHighlights.signedDate && <span className="text-ink-400">{contractHighlights.signedDate}</span>}
                </div>
                <dl className="m-0 flex flex-col gap-1.5">
                  {contractHighlights.items.map((it, i) => (
                    <div key={i}>
                      <dt className="text-ink-400">{it.label}</dt>
                      <dd className={'m-0 ' + (it.tone === 'danger' ? 'text-red-600 font-semibold'
                        : it.tone === 'warn' ? 'text-amber-700' : 'text-ink-800')}>{it.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          )}

          <TeamMembers
            studyId={summary.id}
            canManage={hasPermission(user, 'STUDY_ASSIGN')}
          />

          <div className="card">
            <div className="card-head"><div className="card-title">Dokumentacja</div></div>
            <div className="card-body">
              <p className="text-[12px] text-ink-500 mb-3">
                Wygeneruj dokumentację z draftów AI, metadanymi i profilem geologicznym.
                DOCX jest edytowalny w MS Word/LibreOffice. <strong>Wniosek</strong> to
                edytowalny szablon do podpisu, składany razem z operatem do Wód Polskich.
                ZIP łączy wniosek + operat + załączniki w jedną paczkę gotową do wysyłki.
                Po zakończeniu kroku K12 „Wysyłka" PDF jest automatycznie archiwizowany
                w MinIO z hashem SHA-256 i retencją 25 lat (PGG art. 93).
              </p>
              <div className="flex flex-col gap-2">
                <a
                  href={`/api/pdf/preview/${summary.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm w-full justify-center"
                >
                  <Icon name="eye" className="w-3.5 h-3.5"/> Podgląd PDF (nowa karta)
                </a>
                <a
                  href={`/api/pdf/${summary.id}/docx`}
                  className="btn btn-secondary btn-sm w-full justify-center"
                >
                  <Icon name="download" className="w-3.5 h-3.5"/> Pobierz DOCX (edytowalny)
                </a>
                <a
                  href={`/api/pdf/${summary.id}/wniosek-docx`}
                  className="btn btn-secondary btn-sm w-full justify-center"
                  title="Wniosek o pozwolenie wodnoprawne (DOCX) — szablon do podpisu, wysyłany do Wód Polskich razem z operatem"
                >
                  <Icon name="download" className="w-3.5 h-3.5"/> Pobierz wniosek (DOCX)
                </a>
                {summary.typeAbbreviation === 'Op-wod' && (
                  <>
                    <a
                      href={`/api/pdf/${summary.id}/plain-description-docx`}
                      className="btn btn-secondary btn-sm w-full justify-center"
                      title="Opis prowadzenia zamierzonej działalności bez terminów specjalistycznych (art. 407 ust. 2 pkt 2 PrW) — generowany przez AI z zaakceptowanych sekcji operatu"
                    >
                      <Icon name="sparkles" className="w-3.5 h-3.5"/> Generuj opis bez terminów (DOCX)
                    </a>
                    <a
                      href={`/api/pdf/${summary.id}/operat-map.png`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm w-full justify-center"
                      title="Mapa sytuacyjno-wysokościowa ujęcia: lokalizacja, zasięg oddziaływania R, działki — część graficzna operatu wg art. 409 ust. 2 PrW"
                    >
                      <Icon name="eye" className="w-3.5 h-3.5"/> Mapa syt-wys (PNG)
                    </a>
                  </>
                )}
                <a
                  href={`/api/pdf/${summary.id}/bundle`}
                  className="btn btn-primary btn-sm w-full justify-center"
                  title="ZIP zawierający wniosek + operat (PDF + DOCX) oraz wszystkie załączniki graficzne i tekstowe z dokumentów wejściowych"
                >
                  <Icon name="download" className="w-3.5 h-3.5"/> Pobierz kompletny operat (ZIP)
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BundleManifestCard studyId={summary.id}/>
    </div>
  );
}

function RejectionsCard({ studyId, readOnly }: { studyId: number; readOnly: boolean }) {
  const [list, setList] = useState<StudyRejectionDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | 'new' | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState('');

  const load = () => {
    studyRejections.list(studyId)
      .then(setList)
      .catch(e => setErr(String(e)));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  const onCreate = async () => {
    setBusy('new');
    try {
      await studyRejections.create(studyId, reason.trim());
      setReason('');
      setShowForm(false);
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  };

  const onResolve = async (rid: number) => {
    setBusy(rid);
    try {
      await studyRejections.resolve(rid);
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (rid: number) => {
    if (!confirm('Usunąć ten wpis o odrzuceniu? Operacja nieodwracalna.')) return;
    setBusy(rid);
    try {
      await studyRejections.remove(rid);
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  };

  const open = (list ?? []).filter(r => !r.resolvedAt);
  const resolved = (list ?? []).filter(r => r.resolvedAt);

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Odrzucenia od ZZ</div>
        {list && (
          <span className="ml-2 text-[12px] text-ink-400">
            {open.length > 0 && <span className="text-amber-700 font-semibold">{open.length} otwartych</span>}
            {open.length > 0 && resolved.length > 0 && <span> · </span>}
            {resolved.length > 0 && <span>{resolved.length} rozwiązanych</span>}
            {list.length === 0 && <span>brak wpisów</span>}
          </span>
        )}
        {!readOnly && !showForm && (
          <button onClick={() => setShowForm(true)} className="ml-auto btn btn-secondary btn-sm">
            <Icon name="plus" className="w-3 h-3"/> Zarejestruj odrzucenie
          </button>
        )}
      </div>
      <div className="card-body !p-0">
        {err && <div className="p-3 text-red-500 text-[12px]">{err}</div>}

        {showForm && (
          <div className="p-4 border-b border-ink-100 bg-amber-50/40">
            <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5 font-semibold">
              Powód odrzucenia (opcjonalnie)
            </div>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="np. brak załącznika z mapą sytuacyjną, nieprawidłowe dane z monitoringu…"
              className="input w-full text-[13px]"
            />
            <div className="flex gap-2 mt-2 justify-end">
              <button
                onClick={() => { setShowForm(false); setReason(''); }}
                className="btn btn-secondary btn-sm"
                disabled={busy === 'new'}
              >Anuluj</button>
              <button
                onClick={onCreate}
                className="btn btn-primary btn-sm"
                disabled={busy === 'new'}
              >
                {busy === 'new' ? 'Zapisywanie…' : 'Zarejestruj odrzucenie'}
              </button>
            </div>
          </div>
        )}

        {!list && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
        {list && list.length === 0 && !showForm && (
          <div className="p-4 text-ink-400 text-[12px] italic">
            Brak odrzuceń. Jeśli ZZ zwróci opracowanie do poprawy — zarejestruj je tutaj.
          </div>
        )}

        {list && list.length > 0 && (
          <table className="table-hw">
            <thead>
              <tr>
                <th>Data odrzucenia</th>
                <th>ZZ</th>
                <th>Powód</th>
                <th>Status</th>
                <th>Zarejestrował</th>
                <th className="text-right">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => {
                const isOpen = !r.resolvedAt;
                return (
                  <tr key={r.id} className={isOpen ? 'bg-amber-50/30' : ''}>
                    <td className="text-[12px] font-mono whitespace-nowrap">
                      {formatDateTime(r.rejectedAt) ?? '—'}
                    </td>
                    <td className="text-[12px]">
                      {r.zarzadZlewniId
                        ? <NavLink to={`/admin/zarzady-zlewni/${r.zarzadZlewniId}`} className="text-water-700 hover:underline">
                            {r.zarzadZlewniName}
                          </NavLink>
                        : <span className="text-ink-400 italic">brak (ujęcie nieprzypisane)</span>}
                    </td>
                    <td className="text-[12px] max-w-[480px]">
                      {r.reason
                        ? <span className="text-ink-700 whitespace-pre-wrap">{r.reason}</span>
                        : <span className="text-ink-400 italic">— bez powodu —</span>}
                    </td>
                    <td>
                      {isOpen
                        ? <span className="pill pill-amber">otwarte</span>
                        : <span className="pill pill-green">rozwiązane {formatDateTime(r.resolvedAt) ?? ''}</span>}
                    </td>
                    <td className="text-[11px] text-ink-500">{r.createdByName ?? '—'}</td>
                    <td className="text-right">
                      {!readOnly && isOpen && (
                        <button
                          onClick={() => onResolve(r.id)}
                          disabled={busy === r.id}
                          className="btn btn-secondary btn-sm mr-1"
                          title="Oznacz jako rozwiązane (ZZ zaakceptował poprawkę)"
                        >Rozwiąż</button>
                      )}
                      {!readOnly && (
                        <button
                          onClick={() => onDelete(r.id)}
                          disabled={busy === r.id}
                          className="text-ink-400 hover:text-red-600 p-1"
                          title="Usuń wpis (np. omyłkowo zarejestrowane)"
                        >
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AiVerificationCard({ studyId, readOnly }: { studyId: number; readOnly: boolean }) {
  const [report, setReport] = useState<AiVerificationReport | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [busyIdx, setBusyIdx] = useState<number | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    studies.lastAiVerification(studyId)
      .then(r => setReport(r))
      .catch(() => { /* nonfatal */ });
  }, [studyId]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const onRun = async () => {
    setRunning(true); setErr(null); setInfo(null); setTick(0);
    try {
      const r = await studies.runAiVerification(studyId);
      setReport(r);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setRunning(false); }
  };

  const onAccept = async (idx: number) => {
    setBusyIdx(idx); setErr(null); setInfo(null);
    try {
      const res = await studies.acceptVerificationIssue(studyId, idx);
      setReport(res.report);
      setInfo(res.applied
        ? 'Poprawka została zaaplikowana do draftu.'
        : 'Uwagę oznaczono jako zaakceptowaną, ale nie udało się automatycznie zastąpić tekstu (fragment niejednoznaczny lub nie został znaleziony). Edytuj sekcję ręcznie w edytorze AI.');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusyIdx(null); }
  };

  const onReject = async (idx: number) => {
    setBusyIdx(idx); setErr(null); setInfo(null);
    try {
      const r = await studies.rejectVerificationIssue(studyId, idx);
      setReport(r);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusyIdx(null); }
  };

  const sevColor = (s: string) =>
    s === 'high' ? 'bg-red-100 text-red-700'
    : s === 'medium' ? 'bg-amber-100 text-amber-700'
    : 'bg-ink-100 text-ink-600';

  const pendingCount = report?.issues.filter(i => i.status === 'pending').length ?? 0;
  const acceptedCount = report?.issues.filter(i => i.status === 'accepted' || i.status === 'accepted-no-apply').length ?? 0;
  const rejectedCount = report?.issues.filter(i => i.status === 'rejected').length ?? 0;

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Weryfikacja AI (krok K10B)</div>
        {report?.runAt && (
          <span className="ml-3 text-[11px] text-ink-400 font-mono">
            ostatnio: {new Date(report.runAt).toLocaleString('pl-PL')}
            {report.tookMs > 0 && <> · {(report.tookMs / 1000).toFixed(1)}s</>}
          </span>
        )}
        {!readOnly && (
          <button
            onClick={onRun}
            disabled={running}
            className={'ml-auto btn btn-sm ' + (report ? 'btn-secondary' : 'btn-primary')}
            title="Uruchamia LLM-weryfikację draftów: daty, podstawy prawne, wyliczenia, ślady AI"
          >
            {running ? (
              <>
                <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"/>
                Weryfikuję… {tick}s
              </>
            ) : report ? (
              <><Icon name="sparkles" className="w-3.5 h-3.5"/> Uruchom ponownie</>
            ) : (
              <><Icon name="sparkles" className="w-3.5 h-3.5"/> Uruchom weryfikację AI</>
            )}
          </button>
        )}
      </div>
      <div className="card-body">
        {!report && !running && (
          <p className="text-[12px] text-ink-500">
            Końcowa weryfikacja kompletu draftów przez LLM przed podpisem. AI sprawdza poprawność dat,
            podstaw prawnych, wyliczeń (Q), spójność, obecność śladów generacji oraz przedstawia
            <strong> propozycje poprawek</strong>. Każdą uwagę możesz zaakceptować (auto-poprawa
            w drafcie) albo odrzucić — finalna decyzja zawsze należy do geologa.
          </p>
        )}
        {err && (
          <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-[12px] mb-3">
            Błąd: {err}
          </div>
        )}
        {info && (
          <div className="p-2 bg-water-50 border border-water-200 rounded text-water-800 text-[12px] mb-3">
            {info}
          </div>
        )}
        {running && (
          <div className="text-[12px] text-ink-500">
            Wywołuję model LLM z kompletem draftów — typowy czas 15–40 s.
          </div>
        )}
        {report && !running && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={'pill ' + (report.passed ? 'pill-green' : 'pill-amber')}>
                {report.passed ? '✓ PASSED' : '⚠ FAILED'}
              </span>
              <span className="text-[11px] text-ink-400 font-mono">
                {report.provider}/{report.model}
              </span>
              {report.issues.length > 0 && (
                <span className="text-[12px] text-ink-600">
                  {pendingCount > 0 && <span className="text-amber-700">{pendingCount} oczekuje · </span>}
                  {acceptedCount > 0 && <span className="text-green-700">{acceptedCount} zaakceptowano · </span>}
                  {rejectedCount > 0 && <span className="text-ink-500">{rejectedCount} odrzucono</span>}
                </span>
              )}
            </div>

            {report.summary && (
              <div className="text-[13px] text-ink-800 bg-ink-50/60 border border-ink-100 rounded p-2.5">
                {report.summary}
              </div>
            )}

            {report.aiTracesFound && report.aiTracesFound.trim() && (
              <div className="border-l-[3px] border-red-500 bg-red-50 p-3 rounded">
                <div className="text-[12px] font-semibold text-red-800 mb-1">
                  ⚠ Ślady generacji AI w treści:
                </div>
                <div className="text-[12px] text-red-700 whitespace-pre-wrap">
                  {report.aiTracesFound}
                </div>
                <div className="text-[11px] text-red-600 italic mt-1">
                  Te fragmenty należy usunąć/poprawić przed podpisem (lub zaakceptować poprawki poniżej).
                </div>
              </div>
            )}

            {report.issues.length > 0 && (
              <div>
                <div className="text-[12px] font-semibold text-ink-700 mb-2">Uwagi szczegółowe — decyzja geologa:</div>
                <ul className="space-y-3">
                  {report.issues.map((iss, i) => (
                    <IssueRow
                      key={i}
                      issue={iss}
                      idx={i}
                      busy={busyIdx === i}
                      readOnly={readOnly}
                      sevColorFn={sevColor}
                      onAccept={() => onAccept(i)}
                      onReject={() => onReject(i)}
                    />
                  ))}
                </ul>
              </div>
            )}

            {report.passed && report.issues.length === 0 && !report.aiTracesFound.trim() && (
              <div className="border-l-[3px] border-green-500 bg-green-50 p-3 rounded text-[12px] text-green-800">
                Brak uwag. Dokumentacja gotowa do podpisu.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CompletenessCard({ studyId, typeCode }: { studyId: number; typeCode: string }) {
  const [report, setReport] = useState<CompletenessReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setErr(null);
    studies.completenessCheck(studyId)
      .then(r => setReport(r))
      .catch(e => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  const sevPill = (s: CompletenessIssue['severity']) =>
    s === 'ERROR' ? 'bg-red-100 text-red-700 border-red-200'
    : s === 'WARNING' ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-blue-50 text-blue-700 border-blue-200';

  const sevLabel = (s: CompletenessIssue['severity']) =>
    s === 'ERROR' ? 'BŁĄD' : s === 'WARNING' ? 'OSTRZEŻENIE' : 'INFO';

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Kompletność operatu wodnoprawnego (art. 409 PrW)</div>
        <span className="ml-2 text-[12px] text-ink-400">
          {typeCode === '3a' && 'OP_WOD_3A — urządzenia wodne'}
          {typeCode === '3b' && 'OP_WOD_3B — pobór wód'}
          {typeCode === '3c' && 'OP_WOD_3C — odprowadzenie ścieków'}
        </span>
        {report && (
          <span className="ml-3 inline-flex gap-1.5 items-center">
            {report.errors > 0 && (
              <span className="pill bg-red-100 text-red-700">{report.errors} błędów</span>
            )}
            {report.warnings > 0 && (
              <span className="pill bg-amber-100 text-amber-700">{report.warnings} ostrzeżeń</span>
            )}
            {report.errors === 0 && report.warnings === 0 && (
              <span className="pill pill-green">Kompletny</span>
            )}
          </span>
        )}
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto btn btn-secondary btn-sm"
          title="Ponowna walidacja"
        >
          {loading ? 'Sprawdzam…' : 'Odśwież'}
        </button>
      </div>
      <div className="card-body">
        {err && <div className="text-red-600 text-[12px] mb-2">{err}</div>}
        {!report && !loading && (
          <p className="text-[12px] text-ink-500">Walidator sprawdza zaakceptowane sekcje, checklistę dokumentów, dane techniczne (Q, R, JCWPd), działki i okres pozwolenia. Błędy blokują przejście do K11 (Podpis kwalifikowany).</p>
        )}
        {report && report.issues.length === 0 && (
          <div className="text-[13px] text-green-700 flex items-center gap-2">
            <Icon name="check" className="w-4 h-4"/> Operat kompletny — można przejść do K11 (Podpis kwalifikowany).
          </div>
        )}
        {report && report.issues.length > 0 && (
          <ul className="flex flex-col gap-2 text-[13px]">
            {report.issues.map((i, idx) => (
              <ValidationIssueRow key={idx} issue={i} studyId={studyId} onChanged={load}
                                    sevPill={sevPill} sevLabel={sevLabel}/>
            ))}
          </ul>
        )}
        {report && !report.canProceedToSign && (
          <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded text-[12px] text-red-700">
            <strong>Krok K11 zablokowany.</strong> Popraw wszystkie błędy (ERROR), a następnie odśwież walidację.
            Ostrzeżenia (WARNING) i informacje (INFO) nie blokują przejścia.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Wiersz pojedynczego błędu walidacji z menu akcji: "Zignoruj" (oznacz jako rozwiązany
 * poza systemem) lub "Dodaj wskazówkę AI" (instrukcja jak AI ma poradzić sobie z brakiem
 * danych). Już nadpisane błędy mają badge "ZIGNOROWANE" / "AI: ..." i przycisk "Cofnij".
 */
function ValidationIssueRow({ issue, studyId, onChanged, sevPill, sevLabel }: {
  issue: CompletenessIssue;
  studyId: number;
  onChanged: () => void;
  sevPill: (s: CompletenessIssue['severity']) => string;
  sevLabel: (s: CompletenessIssue['severity']) => string;
}) {
  const [aiHintOpen, setAiHintOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ignore = async () => {
    if (!confirm(`Zignorować błąd "${issue.code}"? Walidator nie będzie blokował K11 dla tego punktu.`)) return;
    setBusy(true); setErr(null);
    try {
      await studies.upsertValidationOverride(studyId, { code: issue.code, action: 'IGNORE' });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const removeOverride = async () => {
    if (!confirm(`Cofnąć nadpisanie dla "${issue.code}"?`)) return;
    setBusy(true); setErr(null);
    try {
      await studies.deleteValidationOverride(studyId, issue.code);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <>
      <li className={'border-l-[3px] pl-3 py-1.5 rounded-sm ' + sevPill(issue.severity).replace('border-', 'border-l-')
            + (issue.overridden ? ' opacity-70 bg-ink-50' : '')}>
        <div className="flex items-start gap-2">
          <span className={'text-[10px] font-bold px-1.5 py-0.5 rounded ' + sevPill(issue.severity)}>
            {sevLabel(issue.severity)}
          </span>
          {issue.overridden && issue.overrideAction === 'IGNORE' && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-ink-200 text-ink-700">
              ZIGNOROWANE
            </span>
          )}
          {issue.overridden && issue.overrideAction === 'AI_HINT' && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-ai-100 text-ai-700">
              AI HINT
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className={issue.overridden ? 'text-ink-600 line-through' : 'text-ink-800'}>
              {issue.message}
            </div>
            {issue.hint && !issue.overridden && (
              <div className="text-[11px] text-ink-500 mt-0.5 italic">→ {issue.hint}</div>
            )}
            {issue.overridden && issue.aiPromptHint && (
              <div className="text-[11px] text-ai-700 mt-0.5 italic">
                <Icon name="sparkles" className="inline w-3 h-3 mr-0.5"/>
                Wskazówka dla AI: {issue.aiPromptHint}
              </div>
            )}
            {issue.overridden && issue.overrideNote && (
              <div className="text-[11px] text-ink-500 mt-0.5">Notatka: {issue.overrideNote}</div>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {!issue.overridden && issue.severity !== 'INFO' && (
              <>
                <button onClick={ignore} disabled={busy}
                        className="btn btn-ghost btn-xs text-ink-600 hover:text-ink-900"
                        title="Zignoruj — nie blokuje K11">Zignoruj</button>
                <button onClick={() => setAiHintOpen(true)} disabled={busy}
                        className="btn btn-ghost btn-xs text-ai-600 hover:text-ai-700"
                        title="Dodaj wskazówkę dla AI">+ AI hint</button>
              </>
            )}
            {issue.overridden && (
              <button onClick={removeOverride} disabled={busy}
                      className="btn btn-ghost btn-xs text-ink-500 hover:text-ink-800"
                      title="Cofnij nadpisanie">Cofnij</button>
            )}
            <span className="font-mono text-[10px] text-ink-400">{issue.code}</span>
          </div>
        </div>
        {err && <div className="text-red-600 text-[11px] mt-1 ml-12">{err}</div>}
      </li>
      {aiHintOpen && (
        <AiHintModal
          issue={issue}
          onCancel={() => setAiHintOpen(false)}
          onSaved={() => { setAiHintOpen(false); onChanged(); }}
          onSave={async (prompt, note) => {
            await studies.upsertValidationOverride(studyId, {
              code: issue.code, action: 'AI_HINT', aiPrompt: prompt, note,
            });
          }}
        />
      )}
    </>
  );
}

function AiHintModal({ issue, onCancel, onSaved, onSave }: {
  issue: CompletenessIssue;
  onCancel: () => void;
  onSaved: () => void;
  onSave: (prompt: string, note: string) => Promise<void>;
}) {
  const [prompt, setPrompt] = useState(issue.aiPromptHint ?? '');
  const [note, setNote] = useState(issue.overrideNote ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!prompt.trim()) { setErr('Wskazówka dla AI jest wymagana'); return; }
    setBusy(true); setErr(null);
    try {
      await onSave(prompt.trim(), note.trim());
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { prompt, note }, onClose: onCancel, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-[600px]">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900 flex items-center gap-2">
            <Icon name="sparkles" className="w-4 h-4 text-ai-600"/>
            Wskazówka dla AI — jak obejść brak danych
          </h2>
          <div className="text-[11px] text-ink-500 mt-1">
            Kod walidacji: <span className="font-mono">{issue.code}</span> · {issue.message}
          </div>
        </div>
        <div className="p-5 grid gap-3">
          <label className="field">
            <span className="field-label">Co AI ma zrobić z brakiem tych danych? *</span>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} className="textarea" rows={5}
                      placeholder={
                        issue.code === 'JCWPD_MISSING'
                          ? 'np. JCWPd nie zostało zidentyfikowane. W sekcji pomiń tabelę i napisz: "JCWPd zostanie określone w toku procedury — wniosek wnoszony przed publikacją zaktualizowanej klasyfikacji".'
                          : 'np. Wpisz konkretną instrukcję dla AI jak ma poradzić sobie z brakiem tych danych w generowanych sekcjach.'
                      }/>
            <span className="text-[10px] text-ink-400 mt-1">
              Wskazówka będzie dołączona do każdej generacji AI dla tego opracowania, jako "INSTRUKCJE OD GEOLOGA".
            </span>
          </label>
          <label className="field">
            <span className="field-label">Notatka wewnętrzna (opcjonalna)</span>
            <input value={note} onChange={e => setNote(e.target.value)} className="input"
                   placeholder="Uzasadnienie dla audytu"/>
          </label>
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : 'Zapisz wskazówkę'}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueRow({ issue, idx, busy, readOnly, sevColorFn, onAccept, onReject }: {
  issue: import('@/api/endpoints').AiVerificationIssue;
  idx: number;
  busy: boolean;
  readOnly: boolean;
  sevColorFn: (s: string) => string;
  onAccept: () => void;
  onReject: () => void;
}) {
  const isResolved = issue.status === 'accepted' || issue.status === 'accepted-no-apply' || issue.status === 'rejected';
  const hasFix = issue.originalFragment.trim().length > 0 && issue.suggestedFix.trim().length > 0;

  return (
    <li className={'border rounded-md p-3 ' + (
      issue.status === 'accepted' ? 'border-green-200 bg-green-50/40'
      : issue.status === 'accepted-no-apply' ? 'border-amber-200 bg-amber-50/40'
      : issue.status === 'rejected' ? 'border-ink-200 bg-ink-50/40 opacity-70'
      : 'border-ink-200 bg-white')}>
      <div className="flex items-start gap-2 mb-2">
        <span className={'pill text-[10px] shrink-0 ' + sevColorFn(issue.severity)}>
          {issue.severity.toUpperCase()}
        </span>
        <span className="text-[11px] text-ink-500 font-mono shrink-0">#{idx + 1}</span>
        {issue.section && (
          <span className="text-[11px] text-ink-700 font-semibold">{issue.section}</span>
        )}
        {issue.status === 'accepted' && (
          <span className="ml-auto pill pill-green text-[10px]">✓ zaakceptowano · auto-poprawa</span>
        )}
        {issue.status === 'accepted-no-apply' && (
          <span className="ml-auto pill text-[10px] bg-amber-100 text-amber-800">✓ zaakceptowano · ręczna edycja</span>
        )}
        {issue.status === 'rejected' && (
          <span className="ml-auto pill text-[10px] bg-ink-100 text-ink-600">× odrzucono</span>
        )}
      </div>
      <div className="text-[12px] text-ink-800 mb-2">{issue.description}</div>

      {hasFix && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div className="border-l-[3px] border-red-300 bg-red-50/50 p-2 text-[12px]">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1">
              Aktualnie w drafcie
            </div>
            <div className="font-mono text-red-900 whitespace-pre-wrap text-[11px]">
              {issue.originalFragment}
            </div>
          </div>
          <div className="border-l-[3px] border-green-400 bg-green-50/50 p-2 text-[12px]">
            <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold mb-1">
              Proponowana zmiana
            </div>
            <div className="font-mono text-green-900 whitespace-pre-wrap text-[11px]">
              {issue.suggestedFix}
            </div>
          </div>
        </div>
      )}

      {!readOnly && !isResolved && (
        <div className="flex gap-2 items-center">
          <button onClick={onAccept} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? (
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"/>
            ) : (
              <Icon name="check" className="w-3 h-3"/>
            )}
            {hasFix ? ' Akceptuj zmianę' : ' Oznacz jako obsłużone'}
          </button>
          <button onClick={onReject} disabled={busy} className="btn btn-secondary btn-sm text-ink-600">
            <Icon name="x" className="w-3 h-3"/> Odrzuć
          </button>
          {!hasFix && (
            <span className="text-[11px] text-ink-400 italic ml-auto">
              brak konkretnej propozycji — tylko ogólna uwaga
            </span>
          )}
        </div>
      )}
    </li>
  );
}

function fmtSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

const CATEGORY_META: Record<BundleManifestEntry['category'], { label: string; iconBg: string; iconColor: string; icon: string }> = {
  WNIOSEK_DOCX: { label: 'Wniosek o pozwolenie wodnoprawne', iconBg: 'bg-green-100', iconColor: 'text-green-700', icon: 'file-text' },
  OPERAT_PDF:  { label: 'Operat — PDF (finalny)',     iconBg: 'bg-red-100',     iconColor: 'text-red-700',     icon: 'file-text' },
  OPERAT_DOCX: { label: 'Operat — DOCX (edytowalny)', iconBg: 'bg-water-100',   iconColor: 'text-water-700',   icon: 'file-text' },
  GRAPHICAL:   { label: 'Załącznik graficzny',         iconBg: 'bg-purple-100',  iconColor: 'text-purple-700',  icon: 'map' },
  TEXTUAL:     { label: 'Załącznik tekstowy',          iconBg: 'bg-amber-100',   iconColor: 'text-amber-700',   icon: 'file-text' },
  README:      { label: 'README — opis paczki',        iconBg: 'bg-ink-100',     iconColor: 'text-ink-600',     icon: 'check' },
};

function BundleManifestCard({ studyId }: { studyId: number }) {
  const [data, setData] = useState<BundleManifest | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggle = (key: string) => setOpenSections(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  useEffect(() => {
    let mounted = true;
    setData(null); setErr(null);
    setOpenSections(new Set());  // sekcje zwinięte po zmianie opracowania
    pdfApi.bundleManifest(studyId)
      .then(d => { if (mounted) setData(d); })
      .catch(e => { if (mounted) setErr(e?.message ?? String(e)); });
    return () => { mounted = false; };
  }, [studyId]);

  if (err) {
    return (
      <div className="card mt-4">
        <div className="card-body text-red-500 text-[12px]">Błąd: {err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card mt-4">
        <div className="card-body text-ink-400 text-[12px]">Ładowanie planu paczki…</div>
      </div>
    );
  }

  const wniosek = data.entries.filter(e => e.category === 'WNIOSEK_DOCX');
  const operat = data.entries.filter(e => e.category === 'OPERAT_PDF' || e.category === 'OPERAT_DOCX');
  const graphical = data.entries.filter(e => e.category === 'GRAPHICAL');
  const textual = data.entries.filter(e => e.category === 'TEXTUAL');
  const readme = data.entries.find(e => e.category === 'README');

  return (
    <div className="card mt-4">
      <div className="card-head">
        <div className="card-title">Skład finalnego operatu (paczka ZIP)</div>
        <span className="ml-3 text-[11px] text-ink-500 font-mono truncate" title={data.zipFilename}>
          {data.zipFilename}
        </span>
        <span className="ml-auto text-[11px] text-ink-500">
          {data.entries.length} plików · {data.graphicalCount} graf. · {data.textualCount} tekst.
        </span>
      </div>
      <div className="card-body !p-0">
        {data.missingFiles > 0 && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-800">
            Uwaga: {data.missingFiles} {data.missingFiles === 1 ? 'plik nie został' : 'plików nie zostanie'} dołączonych
            (brak bajtów w MinIO — usunięte z archiwum).
          </div>
        )}
        {wniosek.length > 0 && (
          <ManifestSection
            sectionKey="wniosek"
            title="Wniosek do Wód Polskich"
            countLabel={String(wniosek.length)}
            entries={wniosek}
            isOpen={openSections.has('wniosek')}
            onToggle={toggle}
          />
        )}
        <ManifestSection
          sectionKey="operat"
          title="Operat"
          countLabel={String(operat.length)}
          entries={operat}
          isOpen={openSections.has('operat')}
          onToggle={toggle}
        />
        {graphical.length > 0 && (
          <ManifestSection
            sectionKey="graphical"
            title="Załączniki graficzne"
            countLabel={String(graphical.length)}
            entries={graphical}
            isOpen={openSections.has('graphical')}
            onToggle={toggle}
          />
        )}
        {textual.length > 0 && (
          <ManifestSection
            sectionKey="textual"
            title="Załączniki tekstowe"
            countLabel={String(textual.length)}
            entries={textual}
            isOpen={openSections.has('textual')}
            onToggle={toggle}
          />
        )}
        {readme && (
          <ManifestSection
            sectionKey="readme"
            title="Dokumentacja paczki"
            countLabel="1"
            entries={[readme]}
            isOpen={openSections.has('readme')}
            onToggle={toggle}
          />
        )}
        {graphical.length + textual.length === 0 && (
          <div className="px-4 py-3 text-[12px] text-ink-500 italic border-t border-ink-100">
            Brak załączników wejściowych (żaden plik nie został dołączony do pozycji wymaganych dokumentów ze statusem
            „Mamy"). Paczka zawiera tylko sam operat.
          </div>
        )}
      </div>
    </div>
  );
}

function ManifestSection({ sectionKey, title, countLabel, entries, isOpen, onToggle }: {
  sectionKey: string;
  title: string;
  countLabel: string;
  entries: BundleManifestEntry[];
  isOpen: boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="border-b border-ink-100 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(sectionKey)}
        className="w-full px-4 py-2 bg-ink-50/60 hover:bg-ink-50 flex items-center gap-2 text-left"
      >
        <Icon
          name="chevron"
          className={'w-3 h-3 text-ink-400 transition-transform ' + (isOpen ? 'rotate-90' : '')}
        />
        <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-600">{title}</span>
        <span className="text-[11px] text-ink-400 font-mono">({countLabel})</span>
      </button>
      {isOpen && (
        <ul className="divide-y divide-ink-100">
          {entries.map(e => {
            const meta = CATEGORY_META[e.category];
            return (
              <li key={e.zipPath} className="px-4 py-2 flex items-center gap-3 hover:bg-ink-50">
                <div className={'w-7 h-7 rounded grid place-items-center shrink-0 ' + meta.iconBg + ' ' + meta.iconColor}>
                  <Icon name={meta.icon} className="w-3.5 h-3.5"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-ink-900 truncate" title={e.displayName}>{e.displayName}</div>
                  <div className="text-[10px] text-ink-400 font-mono truncate" title={e.zipPath}>{e.zipPath}</div>
                  {e.parentReqName && (
                    <div className="text-[10px] text-ink-500">Z pozycji: {e.parentReqName}</div>
                  )}
                </div>
                {e.sizeBytes != null && (
                  <div className="text-[11px] text-ink-500 font-mono tabular-nums whitespace-nowrap">
                    {fmtSize(e.sizeBytes)}
                  </div>
                )}
                {e.downloadUrl ? (
                  <a
                    href={e.downloadUrl}
                    download
                    className="btn btn-secondary btn-sm"
                    title="Pobierz tylko ten plik"
                    onClick={ev => ev.stopPropagation()}
                  >
                    <Icon name="download" className="w-3 h-3"/>
                  </a>
                ) : (
                  <span className="text-[10px] text-ink-400 italic" title="Plik generowany tylko wewnątrz paczki ZIP">
                    w paczce
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Pętla rewizji: feedback urzędu + lista wersji + diff
// ============================================================================

/**
 * Banner przy statusie REVISION_NEEDED — pokazuje OSTATNIE nierozwiązane uwagi urzędu
 * (z {@link studyRejections.list}), żeby operator widział „co poprawić" bez klikania
 * w osobną kartę. Drafty są edytowalne, K11+K12 zresetowane do TODO.
 */
function RevisionFeedbackBanner({ studyId }: { studyId: number }) {
  const [latest, setLatest] = useState<StudyRejectionDto | null>(null);
  useEffect(() => {
    studyRejections.list(studyId)
      .then(list => {
        const open = list.find(r => !r.resolvedAt);
        setLatest(open ?? list[0] ?? null);
      })
      .catch(() => { /* nonfatal */ });
  }, [studyId]);

  return (
    <div className="mb-4 px-4 py-3 rounded-md border-l-[4px] border-red-500 bg-red-50/60 flex items-start gap-3">
      <Icon name="x" className="w-4 h-4 text-red-700 mt-1"/>
      <div className="flex-1 text-[13px] text-red-900">
        <div className="font-semibold">Urząd zwrócił z prośbą o poprawki — opracowanie wróciło do edycji</div>
        {latest && latest.reason && (
          <div className="text-[12px] text-red-800 mt-1 whitespace-pre-wrap">
            <span className="font-semibold">Uwagi urzędu:</span> {latest.reason}
          </div>
        )}
        <div className="text-[11px] text-red-700 mt-1.5">
          Edytuj drafty (Asystent AI), regeneruj sekcje jeśli trzeba, a następnie ponownie ukończ
          K11 (Podpis) i K12 (Wysyłka) — utworzy się nowa wersja operatu.
        </div>
      </div>
    </div>
  );
}

/**
 * Karta z listą wersji opracowania (Documentation v1, v2…) i selektorem diff'a:
 * dwa dropdowny „od → do", przycisk porównaj, modal z side-by-side per sekcja.
 */
export function StudyVersionsCard({ studyId }: { studyId: number }) {
  const [versions, setVersions] = useState<StudyVersionDto[] | null>(null);
  const [fromId, setFromId] = useState<number | ''>('');
  const [toId, setToId] = useState<number | ''>('');
  const [diff, setDiff] = useState<SectionDiff[] | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    studyReview.versions(studyId)
      .then(v => {
        setVersions(v);
        if (v.length >= 2) {
          // Domyślnie: porównaj najnowszą z poprzednią.
          setToId(v[0].documentationId);
          setFromId(v[1].documentationId);
        }
      })
      .catch(e => setErr(String(e)));
  }, [studyId]);

  const onCompare = () => {
    if (!fromId || !toId || fromId === toId) {
      setErr('Wybierz dwie różne wersje do porównania');
      return;
    }
    setBusy(true);
    setErr(null);
    studyReview.diff(studyId, Number(fromId), Number(toId))
      .then(d => { setDiff(d); setDiffOpen(true); })
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  if (!versions) {
    return (
      <div className="card mb-4">
        <div className="card-head"><div className="card-title">Wersje operatu</div></div>
        <div className="card-body text-[12px] text-ink-400">Ładowanie…</div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Wersje operatu</div>
        <span className="ml-2 text-[11px] text-ink-400">
          {versions.length === 0 ? 'brak — pojawią się po pierwszej wysyłce (K12)' : versions.length + ' wersji'}
        </span>
      </div>
      <div className="card-body">
        {err && <div className="text-red-500 text-[12px] mb-2">Błąd: {err}</div>}
        {versions.length === 0 ? (
          <div className="text-[12px] text-ink-400 italic">
            Po ukończeniu K12 „Wysyłka" tworzy się pierwsza wersja operatu (PDF + snapshot draftów).
            Każda kolejna iteracja po poprawkach dodaje wersję — porównanie pokazuje co się zmieniło między nimi.
          </div>
        ) : (
          <>
            <table className="table-hw mb-3">
              <thead>
                <tr><th>Wersja</th><th>Data</th><th>Plik</th><th className="text-right">Rozmiar</th><th>Podpis</th></tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.documentationId}>
                    <td className="font-mono text-[12px]">v{v.version}</td>
                    <td className="text-[12px] text-ink-600 whitespace-nowrap">{formatDateTime(v.createdAt) ?? '—'}</td>
                    <td className="text-[12px] truncate max-w-[260px]">{v.fileName ?? '—'}</td>
                    <td className="text-right font-mono text-[11px]">{fmtSize(v.sizeBytes ?? 0)}</td>
                    <td>{v.signed
                      ? <span className="pill pill-green text-[10px]">podpisane</span>
                      : <span className="pill text-[10px]">brak</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {versions.length >= 2 && (
              <div className="flex flex-wrap items-end gap-2 border-t border-ink-100 pt-3">
                <label className="field !flex-none w-[200px]">
                  <span className="field-label">Od (starsza)</span>
                  <SearchableSelect value={fromId === '' ? '' : String(fromId)} onChange={v => setFromId(v === '' ? '' : Number(v))}>
                    <option value="">—</option>
                    {versions.map(v => (
                      <option key={v.documentationId} value={v.documentationId}>v{v.version}</option>
                    ))}
                  </SearchableSelect>
                </label>
                <span className="text-ink-400 text-[20px] mb-1.5">→</span>
                <label className="field !flex-none w-[200px]">
                  <span className="field-label">Do (nowsza)</span>
                  <SearchableSelect value={toId === '' ? '' : String(toId)} onChange={v => setToId(v === '' ? '' : Number(v))}>
                    <option value="">—</option>
                    {versions.map(v => (
                      <option key={v.documentationId} value={v.documentationId}>v{v.version}</option>
                    ))}
                  </SearchableSelect>
                </label>
                <button disabled={busy || !fromId || !toId || fromId === toId} onClick={onCompare}
                        className="btn btn-primary btn-sm">
                  <Icon name="search" className="w-3.5 h-3.5"/>
                  {busy ? 'Porównuję…' : 'Porównaj sekcje'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {diffOpen && diff && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
             onClick={() => setDiffOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-[1400px] w-full mt-8" onClick={e => e.stopPropagation()}>
            <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
              <Icon name="search" className="w-4 h-4 text-water-600"/>
              <h2 className="text-[16px] font-semibold m-0">
                Porównanie wersji: v{versions.find(v => v.documentationId === fromId)?.version} → v{versions.find(v => v.documentationId === toId)?.version}
              </h2>
              <span className="ml-2 text-[11px] text-ink-500">
                {diff.filter(d => d.changed).length} z {diff.length} sekcji zmienione
              </span>
              <button onClick={() => setDiffOpen(false)} className="ml-auto text-ink-400 hover:text-ink-700">
                <Icon name="x"/>
              </button>
            </div>
            <div className="p-4 max-h-[80vh] overflow-auto space-y-4">
              {diff.length === 0 && <div className="text-ink-400 text-[12px] italic">Brak sekcji w tych wersjach.</div>}
              {diff.map(s => (
                <div key={s.section} className={'border rounded ' + (s.changed ? 'border-amber-300 bg-amber-50/30' : 'border-ink-100')}>
                  <div className="px-3 py-2 border-b border-ink-100 flex items-center gap-2">
                    <span className="font-semibold text-[13px] text-ink-900">{s.section}</span>
                    {s.changed
                      ? <span className="pill pill-amber text-[10px]">zmienione</span>
                      : <span className="pill text-[10px]">bez zmian</span>}
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-ink-100">
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-1">Stara wersja</div>
                      <pre className="text-[11px] text-ink-700 whitespace-pre-wrap font-sans">{s.oldText || <span className="italic text-ink-400">(sekcja nieobecna)</span>}</pre>
                    </div>
                    <div className="p-3 bg-water-50/30">
                      <div className="text-[10px] uppercase tracking-wider text-water-700 font-semibold mb-1">Nowa wersja</div>
                      <pre className="text-[11px] text-ink-800 whitespace-pre-wrap font-sans">{s.newText || <span className="italic text-ink-400">(sekcja nieobecna)</span>}</pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
